<template>
  <div
    class="py-4 px-4 h-100"
  >
    <div
      v-if="isPageLoading"
      class="loader mx-auto h-100"
    />
    <template
      v-else
    >
      <section
        class="mb-3"
      >
        <b-row>
          <b-col>
            <base-help-tooltip
              popover-placement="bottom"
            >
              <template v-slot:left>
                <div>
                  <h1 class="page-title">
                    Publisher Performance
                  </h1>
                </div>
              </template>
              <template v-slot:popover-body>
                <p>View a breakdown of the data by individual publishers.</p>
              </template>
            </base-help-tooltip>
          </b-col>
        </b-row>
      </section>
      <section>
        <b-row
          class="mb-2"
        >
          <b-col class="text-right">
            <b-btn
              variant="primary"
              class="ml-2 border-0 justify-content-right"
              data-test="export-creative-data-csv-button"
              @click="downloadAsCSV()"
              :disabled="!publishersStats || !allParametersArePresent"
            >
              Export all creative data as CSV
            </b-btn>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <b-dropdown
              id="publishers-overview-sort-button"
              class="mb-2"
              :text="'Sort By: ' + getSortByValue(sortBy)"
            >
              <b-dropdown-item
                v-for="(option, index) in sortByOptions"
                :key="index"
                @click="updateSortBy(option)"
              >
                {{ getSortByValue(option) }}
              </b-dropdown-item>
            </b-dropdown>
            <base-filter-dropdown
              class="mb-2 ml-2"
              :filter="'publisher'"
              :options-list="campaignPublishersList"
              :key-property="'id'"
              :value-property="'name'"
              :selected-options="selectedPublishersIds"
              @updatedSelectedOptions="updatedSelectedOptions('selectedPublishersIds', ...arguments)"
              @selectAllOptions="selectAllOptions('campaignPublishersList', 'selectedPublishersIds', 'id')"
              @deselectAllOptions="deselectAllOptions('selectedPublishersIds')"
            />
            <base-filter-dropdown
              class="mb-2 ml-2"
              :filter="'creative'"
              :options-list="GET_ACTIVE_CREATIVES"
              :key-property="'id'"
              :value-property="'name'"
              :selected-options="selectedCreativesIds"
              @updatedSelectedOptions="updatedSelectedOptions('selectedCreativesIds', ...arguments)"
              @selectAllOptions="selectAllOptions('GET_ACTIVE_CREATIVES', 'selectedCreativesIds', 'id')"
              @deselectAllOptions="deselectAllOptions('selectedCreativesIds')"
            />
            <base-filter-dropdown
              class="mb-2 ml-2"
              :filter="'content items'"
              :options-list="campaignContentItems"
              :key-property="'encodedExternalId'"
              :value-property="'title'"
              :selected-options="selectedContentItemIds"
              @updatedSelectedOptions="updatedSelectedOptions('selectedContentItemIds', ...arguments)"
              @selectAllOptions="selectAllOptions('campaignContentItems', 'selectedContentItemIds', 'encodedExternalId')"
              @deselectAllOptions="deselectAllOptions('selectedContentItemIds')"
            />
          </b-col>
          <b-col
            cols="auto"
          >
            <date-picker-dropdown
              @updateDates="updateDates"
              right
              toggle-class="text-right"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-alert
            v-if="!dataIsPresent"
            show
            variant="warning"
            class="w-50 mt-5 mx-auto text-center"
          >
            There's no data to show
          </b-alert>

          <b-col
            v-else
            class="col-xl-6 col-xxl-4 col-12"
            v-for="(publisher, index) in sortedPublishersList"
            :key="index"
          >
            <publisher-report-card
              :publisher="publisher"
              :has-scrolls="hasScrollsData"
            />
          </b-col>
        </b-row>
      </section>
    </template>
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapState,
} from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as reportingTypes from '@/modules/reporting/store/types';
import * as contentTypes from '@/modules/content/store/types';

import PublisherReportCard from '@/modules/reporting/components/PublisherReportCard';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';
import BaseFilterDropdown from '@/components/BaseFilterDropdown.vue';
import DatePickerDropdown from '@/components/DatePickerDropdown.vue';
import fileHelper from '@/core/mixins/fileHelper';

import helper from '@/core/helper';

export default {
  name: 'PublisherPerformance',
  components: {
    DatePickerDropdown,
    BaseFilterDropdown,
    BaseHelpTooltip,
    PublisherReportCard,
  },
  mixins: [
    fileHelper,
  ],
  data() {
    return {
      dataIsPresent: true,
      isPageLoading: true,
      campaignPublishersList: [],
      campaignContentItems: [],
      contentList: [],
      creativeList: [],
      publishersStats: [],
      sortBy: 'ctr',
      selectedPublishersIds: [],
      selectedCreativesIds: [],
      selectedContentItemIds: [],
      dateRange: {
        start: -1,
        end: -1,
      },
    };
  },
  computed: {
    ...mapState('campaigns', [
      'order',
      'campaign',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_CAMPAIGN_MIN_START_DATE,
      campaignTypes.GET_CAMPAIGN_MAX_END_DATE,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ACTIVE_CREATIVES,
    ]),
    ...mapState('content', [
      'feedId',
    ]),
    sortedPublishersList() {
      return this.getSortedPublishersList(this.sortBy);
    },
    hasScrollsData() {
      return this.publishersStats.some((stats) => stats.scrolls != null);
    },
    sortByOptions() {
      const withScrolls = [
        'name',
        'impressions',
        'clicks',
        'ctr',
        'scrolls',
        'str',
      ];
      const noScrolls = [
        'name',
        'impressions',
        'clicks',
        'ctr',
      ];
      return this.hasScrollsData ? withScrolls : noScrolls;
    },
    fullPublishersDetails() {
      const publishersDetails = [];
      for (let index = 0; index < this.publishersStats.length; index++) {
        const publisherStats = this.publishersStats[index];
        const publisherInfo = this.campaignPublishersList.find((publisher) => publisherStats.publisherId === publisher.id);
        publishersDetails.push({
          publisherInfo,
          publisherStats,
        });
      }
      return publishersDetails;
    },
    allParametersArePresent() {
      return this.selectedPublishersIds.length > 0 && this.selectedCreativesIds.length > 0 && this.selectedContentItemIds.length > 0;
    },
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.FETCH_PUBLISHERS,
    ]),
    ...mapActions('reporting', [
      reportingTypes.FETCH_PUBLISHER_IDS_WITH_STATS,
      reportingTypes.FETCH_CAMPAIGN_PUBLISHER_STATS,
      reportingTypes.DOWNLOAD_PUBLISHER_REPORT_CSV,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_EXISTING_CREATIVES_AS_SHAREE,
      contentTypes.FETCH_PUBLISHED_CONTENT_ITEMS,
    ]),
    updatedSelectedOptions(selectedOptionsList, id, isChecked) {
      isChecked ? this[selectedOptionsList].push(id)
        : this[selectedOptionsList].splice(this[selectedOptionsList].indexOf(id), 1);
      this.updateReport();
    },
    selectAllOptions(fullList, selectedOptionsList, keyProperty) {
      this[selectedOptionsList] = this[fullList].map((option) => option[keyProperty]);
      this.updateReport();
    },
    deselectAllOptions(selectedOptionsList) {
      this[selectedOptionsList] = [];
      this.dataIsPresent = false;
    },
    updateDates(startDate, endDate) {
      this.dateRange.start = startDate;
      this.dateRange.end = endDate;
      this.updateReport();
    },
    getSortedPublishersList(sortBy) {
      if (sortBy === 'name') {
        return this.fullPublishersDetails.sort((a, b) => a.publisherInfo.name.localeCompare(b.publisherInfo.name));
      }
      return this.fullPublishersDetails.sort((a, b) => b.publisherStats[sortBy] - a.publisherStats[sortBy]);
    },
    fetchPublishers() {
      const payload = {
        all: true,
      };
      return this.FETCH_PUBLISHERS(payload);
    },
    fetchPublisherIdsWithStats() {
      const payload = {
        creativeIds: this.selectedCreativesIds.join(','),
      };
      return this.FETCH_PUBLISHER_IDS_WITH_STATS(payload);
    },
    fetchContentItems() {
      const payload = {
        advertiserId: this.campaign.advertiserId,
        feedId: this.feedId,
      };
      return this.FETCH_PUBLISHED_CONTENT_ITEMS(payload);
    },
    createCampaignContentItems(contentItems) {
      this.campaignContentItems = contentItems;
    },
    createPublisherList(fullPublishersList, publishersIdsWithStats) {
      this.campaignPublishersList = fullPublishersList.flat()
        .filter((publisher) => publishersIdsWithStats.includes(publisher.id));
    },
    getSortByValue(label) {
      if (label === 'ctr' || label === 'str') return `${label.toUpperCase()}%`;
      return label.toUpperCase();
    },
    updateSortBy(sortBy) {
      this.sortBy = sortBy;
    },
    getPublisherStats(publisherIds, creativeIds, contentItemIds, startDate, endDate) {
      const { advertiserId, campaignId } = this.$route.params;
      const payload = {
        advertiserId,
        campaignId,
        body: {
          publisherIds,
          creativeIds,
          contentItemIds,
          fromDate: startDate,
          toDate: endDate,
        },
      };

      this.FETCH_CAMPAIGN_PUBLISHER_STATS(payload)
        .then((publishersStats) => {
          this.publishersStats = publishersStats;
        });
    },
    updateReport: helper.debounce(function fetchData() {
      if (this.allParametersArePresent) {
        this.dataIsPresent = true;
        this.getPublisherStats(this.selectedPublishersIds, this.selectedCreativesIds, this.selectedContentItemIds, this.dateRange.start, this.dateRange.end);
      } else {
        this.dataIsPresent = false;
      }
    }, 1500),
    async downloadAsCSV() {
      const { advertiserId, campaignId } = this.$route.params;
      const payload = {
        advertiserId,
        campaignId,
        body: {
          fromDate: this.dateRange.start === -1 ? this.order.startDate : this.dateRange.start,
          toDate: this.dateRange.end === -1 ? this.order.endDate : this.dateRange.end,
          publisherIds: this.selectedPublishersIds,
          creativeIds: this.selectedCreativesIds,
          contentItemIds: this.selectedContentItemIds,
        },
      };
      const csvContent = await this.DOWNLOAD_PUBLISHER_REPORT_CSV(payload);
      this.downloadCsvFile(csvContent, `${this.campaign.name} - Publisher Performance Report`);
    },
  },
  async created() {
    try {
      const { campaignId } = this.$route.params;
      this.campaignId = campaignId;
      this.selectAllOptions('GET_ACTIVE_CREATIVES', 'selectedCreativesIds', 'id');

      const fetchPublishers = await this.fetchPublishers();
      const fetchPublisherIdsWithStats = this.fetchPublisherIdsWithStats();
      const fetchContentItems = this.fetchContentItems();
      Promise.all([fetchPublishers, fetchPublisherIdsWithStats, fetchContentItems])
        .then(([fullPublishersList, publisherIdsWithStats, contentItems]) => {
          this.createPublisherList(fullPublishersList, publisherIdsWithStats);
          this.createCampaignContentItems(contentItems);
          this.selectAllOptions('campaignPublishersList', 'selectedPublishersIds', 'id');
          this.selectAllOptions('campaignContentItems', 'selectedContentItemIds', 'encodedExternalId');
          this.isContentStatsTableLoading = true;
          this.isPageLoading = false;
        });
      await this.FETCH_EXISTING_CREATIVES_AS_SHAREE(campaignId);
      this.updateDates(this.GET_CAMPAIGN_MIN_START_DATE, this.GET_CAMPAIGN_MAX_END_DATE);
      this.updateReport();
    } catch (err) {
      this.$router.back();
    } finally {
      this.isPageLoading = false;
    }
  },
};
</script>
